// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DetailedAsset } from '@/asset/api/types';
import { currencyEquals } from '@/currency/utilities/math';
import { pluralize } from '@/utilities/pluralize';
import { Theme } from '@mui/material';
import { DateTime } from 'luxon';

export const CHART_HEIGHT_PX = 392;
// Top reserves one label row per MARKER_MAX_ROWS (see MARKER_ROW_HEIGHT_PX)
export const SVG_MARGIN = { top: 102, right: 72, bottom: 52, left: 72 };
export const TOOLTIP_VERTICAL_OFFSET_PX = 20;

// Marker labels stack into rows when they would collide horizontally
export const MARKER_MAX_ROWS = 3;
const MARKER_LABEL_CHAR_WIDTH_PX = 6.5;
const MARKER_LABEL_GAP_PX = 8;

export interface HoverDatum {
  accumulated: number;
  bookValue: number;
  date: DateTime;
  depreciation: number;
  periodEnd: DateTime;
  top: number;
}

export interface NormalizedPeriod {
  accumulated: number;
  bookValue: number;
  date: DateTime;
  depreciation: number;
  index: number;
}

interface ChartPoint {
  date: DateTime;
  value: number;
}

export interface Marker {
  color: string;
  date: DateTime;
  label: string;
  variant: 'current' | 'phase';
}

interface PositionedMarker extends Marker {
  row: number;
  x: number;
}

export function normalizeDepreciationPeriods(
  periods: NonNullable<DetailedAsset['accounting']['depreciationPeriods']>
) {
  return periods.map((period, index) => ({
    accumulated: period.accumulated.amount.toNumber(),
    bookValue: period.bookValue.amount.toNumber(),
    date: period.date.startOf('day'),
    depreciation: period.depreciation.amount.toNumber(),
    index,
  }));
}

export function haveSameInstant(
  prev: DateTime | null,
  next: DateTime | null
): boolean {
  if (prev === next) {
    return true;
  }
  if (prev === null || next === null) {
    return false;
  }
  return prev.valueOf() === next.valueOf();
}

export function haveSamePeriods(
  prev: DetailedAsset['accounting']['depreciationPeriods'],
  next: DetailedAsset['accounting']['depreciationPeriods']
): boolean {
  if (prev === next) {
    return true;
  }
  if (prev === null || next === null) {
    return false;
  }
  if (prev.length !== next.length) {
    return false;
  }
  return prev.every((period, index) => {
    const other = next[index];
    return (
      currencyEquals(period.bookValue, other.bookValue) &&
      currencyEquals(period.depreciation, other.depreciation) &&
      currencyEquals(period.accumulated, other.accumulated) &&
      period.date.valueOf() === other.date.valueOf()
    );
  });
}

export function getAxisMax(values: (number | null)[]) {
  const numericValues = values.filter(
    (value): value is number => value !== null && Number.isFinite(value)
  );
  if (numericValues.length === 0) {
    return 1;
  }

  return Math.max(...numericValues, 1);
}

export function getLabelFrequency(periodCount: number) {
  if (periodCount <= 12) {
    return 1;
  }
  if (periodCount <= 36) {
    return 3;
  }

  return 12;
}

export function getMonthTicks(props: {
  endDate: DateTime;
  frequency: number;
  startDate: DateTime;
}) {
  const months: DateTime[] = [];
  let currentMonth = props.startDate.startOf('month');
  const endMonth = props.endDate.startOf('month');

  while (currentMonth.valueOf() <= endMonth.valueOf()) {
    months.push(currentMonth);
    currentMonth = currentMonth.plus({ months: props.frequency });
  }

  return months;
}

export function getMarkers(props: {
  chartEndDate: DateTime;
  currentDate: DateTime;
  disposalDate: DateTime | null;
  endOfUsefulLife: DateTime | null;
  inServiceDate: DateTime | null;
  inventoryDate: DateTime;
  theme: Theme;
}) {
  const markers: Marker[] = [
    {
      color: props.theme.palette.grey[700],
      date: props.inventoryDate,
      label: 'Inventory',
      variant: 'phase',
    },
  ];

  if (props.inServiceDate !== null) {
    markers.push({
      color: props.theme.palette.grey[700],
      date: props.inServiceDate,
      label: 'In service',
      variant: 'phase',
    });
  }

  if (
    props.endOfUsefulLife !== null &&
    props.endOfUsefulLife.valueOf() <= props.chartEndDate.valueOf()
  ) {
    markers.push({
      color: props.theme.palette.grey[700],
      date: props.endOfUsefulLife,
      label: 'Useful life end',
      variant: 'phase',
    });
  }

  if (props.disposalDate !== null) {
    markers.push({
      color: props.theme.palette.grey[700],
      date: props.disposalDate,
      label: 'Disposal',
      variant: 'phase',
    });
  }

  if (
    props.currentDate.valueOf() >= props.inventoryDate.valueOf() &&
    props.currentDate.valueOf() <= props.chartEndDate.valueOf()
  ) {
    markers.push({
      color: props.theme.palette.warning.main,
      date: props.currentDate,
      label: 'Today',
      variant: 'current',
    });
  }

  return markers;
}

export function layoutMarkers(props: {
  markers: Marker[];
  xScale: (date: Date) => number;
}): PositionedMarker[] {
  const measured = props.markers
    .map((marker) => {
      const x = props.xScale(marker.date.toJSDate());
      const halfWidthPx =
        (marker.label.length * MARKER_LABEL_CHAR_WIDTH_PX +
          MARKER_LABEL_GAP_PX) /
        2;
      return { halfWidthPx, marker, x };
    })
    .sort((a, b) => a.x - b.x);

  const rowRightEdgesPx: number[] = [];
  return measured.map(({ halfWidthPx, marker, x }) => {
    const leftEdgePx = x - halfWidthPx;
    const rightEdgePx = x + halfWidthPx;
    let row = rowRightEdgesPx.findIndex((edgePx) => edgePx <= leftEdgePx);
    if (row === -1) {
      row =
        rowRightEdgesPx.length < MARKER_MAX_ROWS
          ? rowRightEdgesPx.length
          : MARKER_MAX_ROWS - 1;
    }
    rowRightEdgesPx[row] = rightEdgePx;
    return { ...marker, row, x };
  });
}

export function getPostServicePoints(props: {
  capitalizedCost: number;
  chartEndDate: DateTime;
  inServiceDate: DateTime | null;
  periods: NormalizedPeriod[];
}) {
  if (
    props.inServiceDate === null ||
    props.chartEndDate.valueOf() < props.inServiceDate.valueOf()
  ) {
    return [];
  }

  const points: ChartPoint[] = [];
  for (const period of props.periods) {
    const isLastPeriod = period.index === props.periods.length - 1;
    const periodEnd = isLastPeriod
      ? props.chartEndDate
      : props.periods[period.index + 1].date.minus({ days: 1 });
    if (periodEnd.valueOf() < props.inServiceDate.valueOf()) {
      continue;
    }

    if (points.length === 0) {
      points.push({
        date: props.inServiceDate,
        value: props.capitalizedCost,
      });
    }

    points.push({ date: periodEnd, value: period.bookValue });
  }

  return points;
}

export function getTooltipPeriodSubLabel(props: {
  date: DateTime;
  inServiceDate: DateTime | null;
  usefulLifeMonths: number | null;
}) {
  if (
    props.inServiceDate === null ||
    props.date.valueOf() < props.inServiceDate.valueOf()
  ) {
    return 'Inventory period';
  }

  if (props.usefulLifeMonths === null) {
    return 'In service';
  }

  const monthNumber =
    props.date
      .startOf('month')
      .diff(props.inServiceDate.startOf('month'), 'months').months + 1;
  const normalizedMonthNumber = Math.max(1, Math.floor(monthNumber));

  if (normalizedMonthNumber > props.usefulLifeMonths) {
    return 'Final period';
  }

  return `Month ${String(normalizedMonthNumber)} / ${String(
    props.usefulLifeMonths
  )}`;
}

export interface DepreciationExit {
  date: DateTime;
  kind: 'Disposed' | 'Sold';
}

export function getRemainingLifeMonths(props: {
  date: DateTime;
  endOfUsefulLife: DateTime | null;
  exitDate: DateTime | null;
  inServiceDate: DateTime | null;
}) {
  if (props.inServiceDate === null || props.endOfUsefulLife === null) {
    return null;
  }

  // Remaining life is measured at the last day of the period, like accumulated depreciation and net book value.
  const periodEnd = props.date.endOf('month').startOf('day');

  if (periodEnd.valueOf() < props.inServiceDate.startOf('day').valueOf()) {
    return null;
  }

  if (
    props.exitDate !== null &&
    periodEnd.valueOf() >= props.exitDate.startOf('day').valueOf()
  ) {
    return null;
  }

  return Math.max(0, wholeMonthsBetween(periodEnd, props.endOfUsefulLife));
}

// Months are counted between UTC calendar days so a midnight daylight-saving shift cannot shave an hour off the span.
function wholeMonthsBetween(from: DateTime, to: DateTime) {
  return Math.floor(
    toUTCCalendarDate(to).diff(toUTCCalendarDate(from), 'months').months
  );
}

function toUTCCalendarDate(value: DateTime) {
  return DateTime.utc(value.year, value.month, value.day);
}

export function getRemainingLifeSummary(props: {
  currentDate: DateTime;
  endOfUsefulLife: DateTime | null;
  exit: DepreciationExit | null;
  inServiceDate: DateTime | null;
  usefulLifeMonths: number | null;
}) {
  if (
    props.inServiceDate === null ||
    props.usefulLifeMonths === null ||
    props.endOfUsefulLife === null
  ) {
    return null;
  }

  const today = props.currentDate.startOf('day');
  const endOfUsefulLife = props.endOfUsefulLife.startOf('day');

  // A sale or disposal dated after today has not happened yet, so it does not end the remaining life.
  if (
    props.exit !== null &&
    props.exit.date.startOf('day').valueOf() <= today.valueOf()
  ) {
    return `${props.exit.kind} on ${props.exit.date.toLocaleString(
      DateTime.DATE_MED
    )}`;
  }

  if (today.valueOf() > endOfUsefulLife.valueOf()) {
    return `Useful life ended ${endOfUsefulLife.toLocaleString(
      DateTime.DATE_MED
    )}`;
  }

  const monthsLeft = wholeMonthsBetween(today, endOfUsefulLife);
  const remaining =
    monthsLeft === 0
      ? 'less than 1 month'
      : pluralize(monthsLeft, { singular: 'month' });

  return `Remaining useful life: ${remaining} · Ends ${endOfUsefulLife.toLocaleString(
    DateTime.DATE_MED
  )}`;
}

export function getTooltipInitialCostBasis(props: {
  acquisitionCost: number | null;
  capitalizedCost: number | null;
  inServiceDate: DateTime | null;
  tooltipDate: DateTime;
}) {
  if (
    props.inServiceDate !== null &&
    props.tooltipDate.valueOf() >= props.inServiceDate.valueOf()
  ) {
    return props.capitalizedCost ?? props.acquisitionCost ?? 0;
  }

  return props.acquisitionCost ?? props.capitalizedCost ?? 0;
}

export function showCostTransitionDetail(props: {
  acquisitionCost: number | null;
  capitalizedCost: number | null;
  inServiceDate: DateTime | null;
  tooltipDate: DateTime;
}) {
  if (
    props.inServiceDate === null ||
    props.acquisitionCost === null ||
    props.capitalizedCost === null
  ) {
    return false;
  }

  return (
    props.tooltipDate.hasSame(props.inServiceDate, 'month') &&
    props.acquisitionCost !== props.capitalizedCost
  );
}

export function sanitizeFilePart(value: string) {
  return value.replace(/[^a-z0-9_-]/gi, '-').replace(/-+/g, '-');
}
